import type {PricingNode} from "#pricing/pricing-node"
import type {Currency} from "#pricing/types/currency"
import {z} from "zod/v4-mini"

export type ConfiguratorVariant = {
    groupId: string
    groupName: string
    variantId: string
    variantName: string
}

export const configuratorVariantSchema = z.object({
    groupId: z.string(),
    groupName: z.string(),
    variantId: z.string(),
    variantName: z.string(),
})

// A mesh currently present in the configurator scene, grouped by name. Meshes that are not present have no entry.
export type MeshEntry = {
    name: string //StoredMesh name, the identity used for grouping
    path: string //template path of the first occurrence, node labels joined with " / "
    amount: number //number of scene meshes with this name, always >= 1
}

export const meshEntrySchema = z.object({
    name: z.string(),
    path: z.string(),
    amount: z.number(),
})

export type PricedItem = {
    description: string
    sku?: string
    price: number
    currency: Currency
    amount: number
}

export type PricingResult = {
    items: PricedItem[]
    priceIncludesVat: boolean
}

// A resolved article number (SKU) with its summed quantity for the current configuration.
// Currency-independent, so it is returned by the public getSkus() API without a PriceSelector.
export type SkuEntry = {
    sku: string
    amount: number
}

// Selection context for the public pricing API. Currently only currency, but kept as an object so
// future disambiguators (country, customer segment, ...) can be added without breaking callers.
// See shared/packages/pricing/Future-extension.md.
export type PriceSelector = {
    currency?: Currency
}

// A selectable price scope returned by the public pricing API. Object-shaped for the same reason
// as PriceSelector — future dimensions (country, segment, ...) are additive.
export type PriceScope = {
    currency: Currency
}

export type PriceWithCurrency = {
    price: number
    currency: Currency
}

export type PriceEntry = {
    price: number
    priceIncludesVat: boolean
}

export type PriceMap = Map<string, Map<Currency, PriceEntry>>

export type PricingNodegraph = PricingNode<PricedItem[]>
